import {
  Autocomplete,
  InputBase,
  InputAdornment,
  IconButton,
  Box,
  MenuItem,
  Menu,
  Button,
  useTheme,
  Typography,
  FormControl,
  CircularProgress,
  createFilterOptions,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Select as MuiSelect } from '@mui/material';
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/authSlice";
import { fetchProducts } from "../../services/product";
import { setSearchQuery } from "../../redux/SearchSlice";
// !-----------------Search-----------------!



const Search = ({
  placeholder = "Search products...",
  from = "default",
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localQuery, setLocalQuery] = useState('');
  const { token,user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  
  const bgColor = theme.palette.tertiary.main;
  const textColor = theme.palette.tertiary.main;
  const reduxSearchQuery = useSelector((state) => state.search.query);

  useEffect(() => {
    setLocalQuery(reduxSearchQuery);
  }, [reduxSearchQuery]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (localQuery.length > 2) {
        fetchSuggestions(localQuery);
      } else {
        setSuggestions([]);
      }
    }, 50);

    return () => clearTimeout(delayDebounceFn);
  }, [localQuery]);

  const fetchSuggestions = async (query) => {
    try {
      setLoading(true);
      const queryModling = `product_name=${query}&page=1&per_page=8&status=2`;
      const response = await fetchProducts(queryModling);

      const productList = response?.data?.data?.product_list || [];

      
      const filteredProducts = productList.filter((product) => {
        const isLoginRequired = product?.requires_login === 1;
        const isAccessible = !isLoginRequired || (isLoginRequired && token);
        return product?.web_visibility === 1 && isAccessible;
      });

      const suggestionList = filteredProducts.map((product) => ({
        id: product.id,
        name: product.product_name,
        category: product.category_name,
        ...product,
      }));
      setMeta(response?.data?.data?.meta);
      setSuggestions(suggestionList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      setLoading(false);
    }
  };

  const handleSearch = (selectedValue = null) => {
    dispatch(setSearchQuery(localQuery));

    if (selectedValue) {
      navigate(`/product/detail/${selectedValue.id}`, {
        state: {
          product: selectedValue,
          searchQuery: localQuery,
          meta: meta,
        },
      });
    } else {
      navigate('/product', {
        state: {
          searchQuery: localQuery,
          searchResults: suggestions,
        },
      });
    }
  };

  return (
    <Box
      sx={{
        marginTop: "16px",
        marginLeft: "6px",
        py: from === "LandHeader" ? "8px" : "5px",
        px: "8px",
        borderRadius: from === "LandHeader" ? "10px" : "5px",
        display: "flex",
        alignItems: "center",
        width: "100%",
        backgroundColor: theme.palette.secondary.main,
        textColor: theme.palette.tertiary.main,
        fontWeight: 400,
      }}
    >
      <Autocomplete
        filterOptions={(options) => options}
        open={suggestions.length > 0 || loading}
        disableClearable
        options={suggestions}
        loading={loading}
        getOptionLabel={(option) => option.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        inputValue={localQuery}
        onInputChange={(_, newInputValue) => {
          setLocalQuery(newInputValue);
        }}
        onChange={(_, newValue) => {
          if (newValue) {
            handleSearch(newValue);
            setSuggestions([]);
          }
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name} {option.category && `(${option.category})`}
          </li>
        )}
        noOptionsText={
          loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Loading results...</Typography>
            </Box>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center' }}>No products found</Typography>
          )
        }
        renderInput={(params) => (
          <InputBase
            {...params.InputProps}
            {...params}
            placeholder={placeholder}
            sx={{
              flex: 1,
              width: "100%",
              color: textColor,
              px: "4px",
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && localQuery) {
                handleSearch();
                setSuggestions([]);
              }
            }}
            endAdornment={
              loading ? <CircularProgress size={20} /> : params.InputProps.endAdornment
            }
          />
        )}
        sx={{
          width: "100%",
          ".MuiAutocomplete-inputRoot": {
            color: textColor,
            "& .MuiAutocomplete-endAdornment svg": {
              color: textColor,
            },
          },
        }}
      />
    </Box>
  );
};

export default Search;


// !-----------------Select options-----------------!
const SelectCommon = ({ anchorEl, handleClose, data }) => {
  const dispatch=useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const handleMenuItemClick =async (option)=>{
    if (option.path === "/auth/logout") {
     await dispatch(logout());
      navigate("/auth/login");
    }else{
      navigate(option.path);
    }
    handleClose();
  }
  return (
    <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={handleClose}
    MenuListProps={{ onMouseLeave: handleClose }}
    sx={{
      mt: "8px",
      transition: "opacity 0.3s ease-in-out",
      opacity: anchorEl ? 1 : 0,
    }}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "center",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "center",
    }}
  >
    {data.map((option, index) => (
      <MenuItem
        key={index}
        onClick={() => handleMenuItemClick(option)}
        sx={{ transition: "background-color 0.2s ease" }}
      >
        {option.label}
      </MenuItem>
    ))}
  </Menu>
  );
};
// !-----------------Border annimation button-----------------!
const BorderAnnimateButton = ({ action, text, width }) => {
  const theme = useTheme();

  return (
    <Box>
      <Button
        variant="contained"
        size="small"
        sx={{
          height: "40px",
          borderRadius: "0",
          width: width ? width : "auto",
          border: `3px solid ${theme.palette.primary.red}`,
          backgroundColor: theme.palette.primary.main,
          fontWeight: 600,
          fontSize: { xs: "11px", sm: "12px", lg: "13px" },
          "&:hover": {
            opacity: "0.8",
          },
        }}
        onClick={action}
      >
        {text}
      </Button>
    </Box>
  );
};
// !----------------- Common Button -----------------!
const CommonButton = ({ action, text,disabled,loading,sx }) => {
  const theme = useTheme();
  return (
    <Box>
      <Button
        variant="contained"
        size="medium"
        sx={sx?sx:{
          borderRadius: "20px",
          backgroundColor: theme.palette.secondary.main,
          fontWeight: 600,
          fontSize: { xs: "11px", sm: "12px", lg: "13px" },
          "&:hover": {
            opacity: "0.8",
          backgroundColor: theme.palette.secondary.main,
          
          },
        
        }}
        onClick={action}
        disabled={disabled}
        key={loading ? 'loading' : 'idle'}
      >
        {
          loading?(
            <CircularProgress size={20} color="inherit" />
          ):(
            text
          )
        }
      </Button>
    </Box>
  );
};
// !-----------------Common input feild-----------------!
  const CommonInput = ({ placeholder, type, value, name, onChange,sx }) => {
    const theme = useTheme();
    return (
      <Box>
        <InputBase
          placeholder={placeholder}
          type={type}
          value={value}
          name={name}
          onChange={onChange}
          sx={sx?sx:{
            borderRadius: "5px",
            backgroundColor: theme.palette.secondary.lighter,
            padding: "5px",
            width: "100%",
            p: 1,
            fontSize: { xs: "12px", sm: "13px" },
          }}
          
        />
      </Box>
    );
  };
// !-----------------Button with text & icon or for just icon-----------------!
const CommonIconAndWithOutIconButton = ({
  action,
  text,
  icon,
  bgColor,
  height,
  width,
  px,
  py,
  color,
  hoverBg,
  fontSize
}) => {
  const theme = useTheme();
  return (
    <Box>
      <Button
        variant="contained"
        size="large"
        sx={{
          borderRadius: "5px",
          height: height ? height : "40px",
          width: width ? width : "auto",
          minWidth: width ? width : "auto",
          backgroundColor: bgColor ? bgColor : theme.palette.secondary.main,
          color: color?color:theme.palette.tertiary.main,
          py: py?py:0,
          px:px?px:"auto",
          fontSize: fontSize?fontSize:"12px",
          "&:hover": {
            backgroundColor: hoverBg?hoverBg:theme.palette.primary.gray,
            cursor: "pointer",
          },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        onClick={action}
      >
        {icon && icon}
        {text && text}
      </Button>
    </Box>
  );
};
// !-----------------Common File Upload-----------------!
const CommonFileUpload = ({ handleChange, text, icon, name,accept }) => {
  const theme = useTheme();
  return (
    <Button
      component="label"
      variant="contained"
      size="medium"
      sx={{
        borderRadius: "20px",
        padding: "5px 10px",
        textTransform: "none",
        backgroundColor: theme.palette.secondary.main,
        fontWeight: 600,
        fontSize: { xs: "11px", sm: "12px", lg: "13px" },
        "&:hover": {
          opacity: "0.8",
        backgroundColor: theme.palette.secondary.main,
        
        },
      
      }}
      startIcon={icon}
    >
      <Typography>{text}</Typography>
      <input
        type="file"
        hidden
        name={name}
        onChange={handleChange}
        accept={accept}
      />
    </Button>
  );
};
// !-----------------Common Select-----------------!
const CommonSelect = ({ options, value, onChange, name, placeholder,height,disabled, ...rest  }) => {
  const safeOptions = Array.isArray(options) ? options : [];
  const theme = useTheme();

  // Ensure value is always a string
  const safeValue = typeof value === 'string' ? value : '';
  return (
    <FormControl fullWidth>
      <MuiSelect
       sx={{
        height: height?height:"40px",
      }}
        value={safeValue}
        onChange={onChange}
        disabled={disabled}
        name={name}
        displayEmpty
        {...rest}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}
        {safeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
};





export {
  SelectCommon,
  Search,
  BorderAnnimateButton,
  CommonButton,
  CommonInput,
  CommonIconAndWithOutIconButton,
  CommonFileUpload,
  CommonSelect
};
